import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Card,
  CardContent,
  FormControlLabel,
  Switch,
  CircularProgress,
  Grid,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  AlertTitle,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Tooltip,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';
import HistoryIcon from '@mui/icons-material/History';
import AddCommentIcon from '@mui/icons-material/AddComment';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { Cancel } from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
// import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { materialDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ErrorMessage from '../../components/common/ErrorMessage';
import StatsChart from '../../components/charts/StatsChart';
import chatApi from '../../api/chatApi';
import knowledgeBaseApi from '../../api/knowledgeBaseApi';
import modelsApi from '../../api/modelsApi';
import settingsApi from '../../api/settingsApi';
import LaunchIcon from '@mui/icons-material/Launch';
import { useGuide } from '../../context/GuideContext';
import { useLocation } from 'react-router-dom';

// 유틸리티 함수들을 컴포넌트 외부로 이동
// 콘텐츠에 HTML이 포함되어 있는지 확인
const hasHtmlTags = (content) => {
  return /<\/?[a-z][\s\S]*>/i.test(content);
};

// 마크다운 테이블이 있는지 확인
const hasMarkdownTable = (content) => {
  if (!content || !content.includes('|')) return false;
  
  const lines = content.split('\n');
  for (let i = 0; i < lines.length - 1; i++) {
    const line = lines[i].trim();
    const nextLine = lines[i + 1].trim();
    
    if (line.startsWith('|') && line.endsWith('|') &&
        nextLine.startsWith('|') && nextLine.endsWith('|') &&
        nextLine.includes('-')) {
      return true;
    }
  }
  
  return false;
};

// 내용이 해시태그로 시작하는지 확인하는 함수
const hasMarkdownHeadings = (content) => {
  return /(?:^|\n)#+\s+/m.test(content);
};

// 🔥 중복 테이블 제거 함수
const removeDuplicateTables = (container) => {
  try {
    console.log('🚀 중복 테이블 제거 함수 시작');
    
    const tables = container.querySelectorAll('table');
    console.log(`📊 발견된 테이블 수: ${tables.length}`);
    
    if (tables.length <= 1) {
      console.log(`ℹ️ 테이블 ${tables.length}개만 있음 - 중복 제거 불필요`);
      return; 
    }
    
    console.log(`🔍 중복 테이블 검사 시작: ${tables.length}개 테이블 발견`);
    
    // 모든 테이블의 전체 내용을 비교하여 중복 감지
    const tableContents = Array.from(tables).map((table, index) => {
      // 테이블의 전체 텍스트 내용을 정규화
      const fullText = table.textContent.replace(/\s+/g, ' ').trim().toLowerCase();
      
      // 헤더 추출
      const headerRow = table.querySelector('thead tr') || table.querySelector('tr:first-child');
      const headers = headerRow ? 
        Array.from(headerRow.querySelectorAll('th, td')).map(cell => cell.textContent.trim()) : [];
      
      // 데이터 행 수 계산
      let dataRows = 0;
      const tbody = table.querySelector('tbody');
      if (tbody) {
        dataRows = tbody.querySelectorAll('tr').length;
      } else {
        const allRows = table.querySelectorAll('tr');
        dataRows = Math.max(0, allRows.length - 1);
      }
      
      console.log(`📋 테이블 ${index + 1} 분석:`, {
        headers: headers,
        dataRows: dataRows,
        contentLength: fullText.length,
        contentPreview: fullText.substring(0, 100) + '...'
      });
      
      return {
        table,
        index,
        headers,
        dataRows,
        fullText,
        element: table
      };
    });
    
    // 1. 전체 내용이 동일한 테이블들 찾기 (완전 중복)
    const contentGroups = {};
    tableContents.forEach(info => {
      const key = info.fullText;
      if (!contentGroups[key]) {
        contentGroups[key] = [];
      }
      contentGroups[key].push(info);
    });
    
    console.log('🔍 내용 기반 그룹화 결과:', Object.keys(contentGroups).map(key => ({
      contentLength: key.length,
      count: contentGroups[key].length,
      dataCounts: contentGroups[key].map(t => t.dataRows)
    })));
    
    // 2. 완전 중복 테이블 제거 (내용이 동일한 경우)
    let totalRemoved = 0;
    Object.keys(contentGroups).forEach(contentKey => {
      const group = contentGroups[contentKey];
      if (group.length > 1) {
        console.log(`⚠️ 완전 중복 테이블 발견 (${group.length}개)!`);
        
        // 가장 많은 데이터를 가진 테이블 유지, 나머지 제거
        const bestTable = group.reduce((best, current) => 
          current.dataRows >= best.dataRows ? current : best
        );
        
        console.log(`🏆 최적 테이블 선택: ${bestTable.dataRows}개 행 (테이블 ${bestTable.index + 1})`);
        
        group.forEach(info => {
          if (info !== bestTable) {
            console.log(`🗑️ 중복 테이블 제거: ${info.dataRows}개 행 (테이블 ${info.index + 1})`);
            if (info.element && info.element.parentNode) {
              info.element.remove();
              totalRemoved++;
            }
          }
        });
      }
    });
    
    // 3. 헤더 구조가 같고 내용이 유사한 테이블들 추가 검사
    const remainingTables = container.querySelectorAll('table');
    if (remainingTables.length > 1) {
      console.log(`🔄 추가 검사: ${remainingTables.length}개 테이블 남음`);
      
      const headerGroups = {};
      Array.from(remainingTables).forEach((table, index) => {
        const headerRow = table.querySelector('thead tr') || table.querySelector('tr:first-child');
        const headers = headerRow ? 
          Array.from(headerRow.querySelectorAll('th, td')).map(cell => cell.textContent.trim()) : [];
        const headerKey = headers.join('|').toLowerCase();
        
        if (!headerGroups[headerKey]) {
          headerGroups[headerKey] = [];
        }
        
        let dataRows = 0;
        const tbody = table.querySelector('tbody');
        if (tbody) {
          dataRows = tbody.querySelectorAll('tr').length;
        } else {
          const allRows = table.querySelectorAll('tr');
          dataRows = Math.max(0, allRows.length - 1);
        }
        
        headerGroups[headerKey].push({
          table,
          index,
          dataRows,
          element: table
        });
      });
      
      Object.keys(headerGroups).forEach(headerKey => {
        const group = headerGroups[headerKey];
        if (group.length > 1) {
          console.log(`📋 헤더 중복 그룹 발견 (${group.length}개):`, headerKey.substring(0, 50) + '...');
          
          const bestTable = group.reduce((best, current) => 
            current.dataRows > best.dataRows ? current : best
          );
          
          group.forEach(info => {
            if (info !== bestTable && info.element && info.element.parentNode) {
              console.log(`🗑️ 헤더 중복 테이블 제거: ${info.dataRows}개 행`);
              info.element.remove();
              totalRemoved++;
            }
          });
        }
      });
    }
    
    console.log(`✅ 중복 제거 완료: 총 ${totalRemoved}개 테이블 제거됨`);
    
    // 최종 결과 확인
    const finalTables = container.querySelectorAll('table');
    console.log(`🎯 최종 테이블 수: ${finalTables.length}개`);
    
  } catch (error) {
    console.error('❌ 중복 테이블 제거 오류:', error);
  }
};

// 마크다운 테이블을 HTML 테이블로 변환
const convertToHtmlTable = (tableLines) => {
  if (tableLines.length < 3) return tableLines.join('\n'); // 최소 헤더, 구분자, 데이터 행이 필요
  
  // 헤더 추출
  const headerCells = tableLines[0]
    .split('|')
    .filter(cell => cell.trim() !== '')
    .map(cell => cell.trim());
  
  // 데이터 행 추출
  const rows = tableLines.slice(2).map(line => 
    line.split('|')
      .filter(cell => cell.trim() !== '')
      .map(cell => cell.trim())
  );
  
  // HTML 테이블 생성 - 인라인 스타일 적용
  let htmlTable = '<table style="width:100%; border-collapse:collapse; margin:16px 0; border:1px solid #e0e0e0; border-radius:4px; overflow:hidden; box-shadow:0 2px 4px rgba(0,0,0,0.1);">';
  
  // 헤더 추가
  htmlTable += '<thead style="background-color:#f5f5f5;"><tr>';
  headerCells.forEach(header => {
    htmlTable += `<th style="padding:12px 16px; text-align:left; border-bottom:2px solid #e0e0e0; font-weight:bold;">${header}</th>`;
  });
  htmlTable += '</tr></thead>';
  
  // 데이터 행 추가
  htmlTable += '<tbody>';
  rows.forEach((row, rowIndex) => {
    // 홀수/짝수 행 배경색 지정
    const bgColor = rowIndex % 2 === 0 ? '#ffffff' : '#fafafa';
    htmlTable += `<tr style="background-color:${bgColor};">`;
    row.forEach(cell => {
      htmlTable += `<td style="padding:8px 16px; border-bottom:1px solid #eeeeee;">${cell}</td>`;
    });
    htmlTable += '</tr>';
  });
  htmlTable += '</tbody></table>';
  
  return htmlTable;
};

// 마크다운 테이블 감지 및 변환 함수
const parseMarkdownTables = (content) => {
  if (!content || !content.includes('|')) return content;
  
  const lines = content.split('\n');
  let result = [];
  let tableLines = [];
  let inTable = false;
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    
    // 테이블 시작 감지: 첫 행이 |로 시작하고 끝나며, 다음 행이 구분자 행인지 확인
    if (!inTable && line.startsWith('|') && line.endsWith('|')) {
      if (i + 1 < lines.length) {
        const nextLine = lines[i + 1].trim();
        if (nextLine.startsWith('|') && nextLine.endsWith('|') && nextLine.includes('-')) {
          inTable = true;
          tableLines = [line];
          continue;
        }
      }
    }
    
    // 테이블 내부 처리
    if (inTable) {
      if (line.startsWith('|') && line.endsWith('|')) {
        tableLines.push(line);
      } else {
        // 테이블 종료, HTML 테이블로 변환
        result.push(convertToHtmlTable(tableLines));
        inTable = false;
        if (line) result.push(line); // 빈 줄이 아니라면 추가
      }
    } else {
      result.push(line);
    }
  }
  
  // 마지막 테이블 처리
  if (inTable && tableLines.length > 0) {
    result.push(convertToHtmlTable(tableLines));
  }
  
  return result.join('\n');
};

// 마크다운 헤딩 처리 (### 형식을 적절한 헤딩으로 변환)
const processMarkdownHeadings = (content) => {
  if (!content) return content;
  
  // 이미 처리된 HTML이면서 변환이 필요없는 경우 건너뜀
  if (hasHtmlTags(content) && !hasMarkdownTable(content) && !content.includes('#') && !content.includes('**')) return content;
  
  // 마크다운 형식 관련 설명 텍스트 제거
  let processedContent = content.replace(/이 표는 마크다운 형식으로 작성되었습니다\./g, '');
  processedContent = processedContent.replace(/마크다운 형식(으로|의|을|을 이용하여|을 사용하여).*?작성/g, '작성');
  
  // 테이블 영역과 비-테이블 영역을 분리하여 처리
  if (hasMarkdownTable(processedContent)) {
    // 줄 단위로 처리
    const lines = processedContent.split('\n');
    let inTable = false;
    let tableStart = 0;
    const processedLines = [];
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      const nextLine = i + 1 < lines.length ? lines[i + 1].trim() : '';
      
      // 테이블 시작 감지
      if (!inTable && line.startsWith('|') && line.endsWith('|') && 
          nextLine.startsWith('|') && nextLine.endsWith('|') && nextLine.includes('-')) {
        inTable = true;
        tableStart = i;
        processedLines.push(lines[i]); // 현재 줄 추가
        continue;
      }
      
      // 테이블 종료 감지
      if (inTable && (!line.startsWith('|') || !line.endsWith('|'))) {
        inTable = false;
        // 테이블 전체를 처리하여 HTML로 변환
        const tableLines = lines.slice(tableStart, i);
        processedLines.pop(); // 마지막으로 추가한 테이블 줄 제거
        processedLines.push(convertToHtmlTable(tableLines));
      }
      
      // 테이블 내부 줄은 그대로 추가
      if (inTable) {
        processedLines.push(lines[i]);
        continue;
      }
      
      // 테이블 외부 영역에서 헤더 및 볼드체 처리
      let processedLine = line;
      
      // 헤더 처리
      if (line.match(/^#{1,6}\s+/)) {
        const headerMatch = line.match(/^(#{1,6})\s+(.+)$/);
        if (headerMatch) {
          const hashes = headerMatch[1];
          const text = headerMatch[2];
          const level = Math.min(hashes.length, 6);
          
          const styles = {
            1: "font-size:20px; font-weight:bold; color:#2c3e50; margin-top:20px; margin-bottom:10px; display:block;",
            2: "font-size:18px; font-weight:bold; color:#2c3e50; margin-top:16px; margin-bottom:8px; display:block;",
            3: "font-size:16px; font-weight:bold; color:#2c3e50; margin-top:14px; margin-bottom:7px; display:block;"
          };
          
          processedLine = `<div style="${styles[level]}">${text}</div>`;
        }
      } 
      // 볼드체 처리 (**text**)
      else if (line.includes('**')) {
        processedLine = line.replace(/\*\*(.*?)\*\*/g, '<strong style="font-weight:bold;">$1</strong>');
      }
      
      processedLines.push(processedLine);
    }
    
    // 마지막 테이블 처리
    if (inTable) {
      const tableLines = lines.slice(tableStart);
      // 이미 추가된 테이블 줄 제거
      processedLines.splice(processedLines.length - (lines.length - tableStart), lines.length - tableStart);
      processedLines.push(convertToHtmlTable(tableLines));
    }
    
    let joinedContent = processedLines.join('\n');
    // 테이블 밖에 있는 볼드체 처리 한번 더 수행
    joinedContent = joinedContent.replace(/\*\*(.*?)\*\*/g, '<strong style="font-weight:bold;">$1</strong>');
    return joinedContent;
  } else {
    // 테이블이 없는 경우 헤더와 볼드체 처리
    // 먼저 헤더 처리
    let result = processedContent.replace(/^(#{1,6})\s+(.+)$/gm, (match, hashes, text) => {
      const level = Math.min(hashes.length, 6);
      
      const styles = {
        1: "font-size:20px; font-weight:bold; color:#2c3e50; margin-top:20px; margin-bottom:10px; display:block;",
        2: "font-size:18px; font-weight:bold; color:#2c3e50; margin-top:16px; margin-bottom:8px; display:block;",
        3: "font-size:16px; font-weight:bold; color:#2c3e50; margin-top:14px; margin-bottom:7px; display:block;"
      };
      
      return `<div style="${styles[level]}">${text}</div>`;
    });
    
    // 볼드체 처리
    result = result.replace(/\*\*(.*?)\*\*/g, '<strong style="font-weight:bold;">$1</strong>');
    
    return result;
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// 🌍 브라우저 언어 자동 감지 & 다국어 UI 지원
// ─────────────────────────────────────────────────────────────────────────────
const SUPPORTED_LANGUAGE_CODES = ['ko', 'en', 'ja', 'zh', 'ru', 'ar', 'es', 'pt', 'mn'];

/** navigator.language 에서 2자리 언어 코드를 추출하고, 지원 목록에 없으면 'ko' 반환 */
const getBrowserLanguage = () => {
  // localStorage에 캐시된 브라우저 언어 감지 설정 확인 (이전 방문 시 저장된 관리자 설정)
  const cachedDetection = localStorage.getItem('sys_browser_language_detection');
  if (cachedDetection === 'false') {
    return 'ko'; // 및라우저 언어 감지 비활성화 → 기본값 한국어
  }
  const raw = (navigator.language || navigator.languages?.[0] || 'ko');
  // "ko-KR" → "ko", "zh-Hant" → "zh" 등 앞 2자리만 사용
  const code = raw.split('-')[0].toLowerCase();
  return SUPPORTED_LANGUAGE_CODES.includes(code) ? code : 'ko';
};

const LANGUAGE_OPTIONS = [
  { code: 'ko', flag: '🇰🇷', label: '한국어 (Korean)' },
  { code: 'en', flag: '🇺🇸', label: 'English (영어)' },
  { code: 'ja', flag: '🇯🇵', label: '日本語 (Japanese)' },
  { code: 'zh', flag: '🇨🇳', label: '中文 (Chinese)' },
  { code: 'ru', flag: '🇷🇺', label: 'Русский (Russian)' },
  { code: 'ar', flag: '🇸🇦', label: 'العربية (Arabic)' },
  { code: 'es', flag: '🇪🇸', label: 'Español (Spanish)' },
  { code: 'pt', flag: '🇵🇹', label: 'Português (Portuguese)' },
  { code: 'mn', flag: '🇲🇳', label: 'Монгол (Mongolian)' },
];

const TRANSLATIONS = {
  ko: {
    greeting: '안녕하세요, 어떤 것을 도와드릴까요?',
    responseLanguage: '응답 언어',
    loadingKakao: '카카오톡 답변 데이터를 불러오는 중...',
    generatingResponse: '응답 생성 중...',
    referenceDocs: '참조 문서',
    page: '페이지',
    accuracy: '정확도',
    noDocsPlaceholder: '질문을 입력하면 관련된 참조 문서가 이 곳에 표시됩니다.',
    kbAnswer: '지식베이스 답변',
    gptAnswer: 'GPT 답변',
    inputPlaceholder: '무엇이든 물어보세요',
    send: '전송',
    alertTitle: '알림',
    confirm: '확인',
    originalUrl: '원본 URL',
    localFile: '로컬 파일',
    viewFile: '파일 보기',
    download: '다운로드',
    kakaoSent: '카카오톡에서 전송된',
    kakaoQuestion: '질문',
    kakaoAnswer: '답변',
    processingTime: '처리시간',
    seconds: '초',
    kakaoMode: '카카오톡 연동 모드',
    kakaoLoaded: '카카오톡에서 전송된 질문의 답변을 불러왔습니다. 이어서 추가 질문을 하실 수 있습니다.',
    kakaoAnswerLoadedAlert: (q) => `카카오톡에서 요청하신 "${q}" 질문의 답변을 불러왔습니다. 추가 질문을 계속하실 수 있습니다.`,
    kakaoLoadError: '카카오톡 답변을 불러오는 중 오류가 발생했습니다. 새로고침 후 다시 시도해주세요.',
  },
  en: {
    greeting: 'We are live and ready to chat with you now. Say something to start a live chat.',
    responseLanguage: 'Response Language',
    loadingKakao: 'Loading KakaoTalk answer data...',
    generatingResponse: 'Generating response...',
    referenceDocs: 'Reference Documents',
    page: 'Page',
    accuracy: 'Accuracy',
    noDocsPlaceholder: 'Related reference documents will appear here once you ask a question.',
    kbAnswer: 'Knowledge Base Answer',
    gptAnswer: 'GPT Answer',
    inputPlaceholder: 'Ask me anything',
    send: 'Send',
    alertTitle: 'Notice',
    confirm: 'OK',
    originalUrl: 'Original URL',
    localFile: 'Local File',
    viewFile: 'View File',
    download: 'Download',
    kakaoSent: 'Sent from KakaoTalk',
    kakaoQuestion: 'question',
    kakaoAnswer: 'answer',
    processingTime: 'Processing time',
    seconds: 's',
    kakaoMode: 'KakaoTalk Integration Mode',
    kakaoLoaded: 'The answer to the question sent from KakaoTalk has been loaded. You can continue asking additional questions.',
    kakaoAnswerLoadedAlert: (q) => `The answer to your KakaoTalk question "${q}" has been loaded. You can continue asking additional questions.`,
    kakaoLoadError: 'Failed to load the KakaoTalk answer. Please refresh and try again.',
  },
  ja: {
    greeting: 'こんにちは！何かお手伝いできますか？',
    responseLanguage: '応答言語',
    loadingKakao: 'カカオトークの回答データを読み込み中...',
    generatingResponse: '回答を生成中...',
    referenceDocs: '参照ドキュメント',
    page: 'ページ',
    accuracy: '精度',
    noDocsPlaceholder: '質問を入力すると、関連する参照ドキュメントがここに表示されます。',
    kbAnswer: 'ナレッジベース回答',
    gptAnswer: 'GPT回答',
    inputPlaceholder: '何でも聞いてください',
    send: '送信',
    alertTitle: 'お知らせ',
    confirm: 'OK',
    originalUrl: '元のURL',
    localFile: 'ローカルファイル',
    viewFile: 'ファイルを見る',
    download: 'ダウンロード',
    kakaoSent: 'カカオトークから送信された',
    kakaoQuestion: '質問',
    kakaoAnswer: '回答',
    processingTime: '処理時間',
    seconds: '秒',
    kakaoMode: 'カカオトーク連携モード',
    kakaoLoaded: 'カカオトークから送信された質問の回答を読み込みました。引き続き追加の質問ができます。',
    kakaoAnswerLoadedAlert: (q) => `カカオトークでのご質問「${q}」の回答を読み込みました。引き続き追加の質問ができます。`,
    kakaoLoadError: 'カカオトークの回答を読み込めませんでした。ページを更新して再試行してください。',
  },
  zh: {
    greeting: '您好！有什么我可以帮您的吗？',
    responseLanguage: '响应语言',
    loadingKakao: '正在加载KakaoTalk回答数据...',
    generatingResponse: '正在生成回答...',
    referenceDocs: '参考文档',
    page: '页面',
    accuracy: '准确率',
    noDocsPlaceholder: '输入问题后，相关参考文档将显示在此处。',
    kbAnswer: '知识库回答',
    gptAnswer: 'GPT回答',
    inputPlaceholder: '请输入任何问题',
    send: '发送',
    alertTitle: '通知',
    confirm: '确认',
    originalUrl: '原始链接',
    localFile: '本地文件',
    viewFile: '查看文件',
    download: '下载',
    kakaoSent: '来自KakaoTalk',
    kakaoQuestion: '问题',
    kakaoAnswer: '回答',
    processingTime: '处理时间',
    seconds: '秒',
    kakaoMode: 'KakaoTalk集成模式',
    kakaoLoaded: '已加载KakaoTalk发送的问题回答。您可以继续提问。',
    kakaoAnswerLoadedAlert: (q) => `已加载您在KakaoTalk中提问"${q}"的回答。您可以继续提问。`,
    kakaoLoadError: '无法加载KakaoTalk回答，请刷新页面后重试。',
  },
  ru: {
    greeting: 'Здравствуйте! Чем я могу вам помочь?',
    responseLanguage: 'Язык ответа',
    loadingKakao: 'Загрузка данных ответа KakaoTalk...',
    generatingResponse: 'Генерация ответа...',
    referenceDocs: 'Справочные документы',
    page: 'Стр.',
    accuracy: 'Точность',
    noDocsPlaceholder: 'После ввода вопроса здесь появятся соответствующие справочные документы.',
    kbAnswer: 'Ответ из базы знаний',
    gptAnswer: 'Ответ GPT',
    inputPlaceholder: 'Задайте любой вопрос',
    send: 'Отправить',
    alertTitle: 'Уведомление',
    confirm: 'OK',
    originalUrl: 'Исходный URL',
    localFile: 'Локальный файл',
    viewFile: 'Просмотр файла',
    download: 'Скачать',
    kakaoSent: 'Отправлено из KakaoTalk',
    kakaoQuestion: 'вопрос',
    kakaoAnswer: 'ответ',
    processingTime: 'Время обработки',
    seconds: 'с',
    kakaoMode: 'Режим интеграции KakaoTalk',
    kakaoLoaded: 'Ответ на вопрос, отправленный из KakaoTalk, загружен. Вы можете задавать дополнительные вопросы.',
    kakaoAnswerLoadedAlert: (q) => `Ответ на ваш вопрос из KakaoTalk «${q}» загружен. Вы можете продолжать задавать вопросы.`,
    kakaoLoadError: 'Не удалось загрузить ответ из KakaoTalk. Обновите страницу и попробуйте снова.',
  },
  ar: {
    greeting: 'مرحباً! كيف يمكنني مساعدتك؟',
    responseLanguage: 'لغة الرد',
    loadingKakao: 'جارٍ تحميل بيانات إجابة KakaoTalk...',
    generatingResponse: 'جارٍ إنشاء الرد...',
    referenceDocs: 'المستندات المرجعية',
    page: 'صفحة',
    accuracy: 'الدقة',
    noDocsPlaceholder: 'ستظهر المستندات المرجعية ذات الصلة هنا عند إدخال سؤال.',
    kbAnswer: 'إجابة قاعدة المعرفة',
    gptAnswer: 'إجابة GPT',
    inputPlaceholder: 'اسألني أي شيء',
    send: 'إرسال',
    alertTitle: 'إشعار',
    confirm: 'موافق',
    originalUrl: 'الرابط الأصلي',
    localFile: 'ملف محلي',
    viewFile: 'عرض الملف',
    download: 'تنزيل',
    kakaoSent: 'مُرسَل من KakaoTalk',
    kakaoQuestion: 'سؤال',
    kakaoAnswer: 'إجابة',
    processingTime: 'وقت المعالجة',
    seconds: 'ث',
    kakaoMode: 'وضع تكامل KakaoTalk',
    kakaoLoaded: 'تم تحميل إجابة السؤال المرسل من KakaoTalk. يمكنك الاستمرار في طرح أسئلة إضافية.',
    kakaoAnswerLoadedAlert: (q) => `تم تحميل إجابة سؤالك "${q}" من KakaoTalk. يمكنك الاستمرار في طرح أسئلة.`,
    kakaoLoadError: 'تعذّر تحميل إجابة KakaoTalk. يُرجى تحديث الصفحة والمحاولة مرة أخرى.',
  },
  es: {
    greeting: '¡Hola! ¿En qué puedo ayudarte?',
    responseLanguage: 'Idioma de respuesta',
    loadingKakao: 'Cargando datos de respuesta de KakaoTalk...',
    generatingResponse: 'Generando respuesta...',
    referenceDocs: 'Documentos de referencia',
    page: 'Página',
    accuracy: 'Precisión',
    noDocsPlaceholder: 'Los documentos de referencia relacionados aparecerán aquí cuando ingrese una pregunta.',
    kbAnswer: 'Respuesta de base de conocimiento',
    gptAnswer: 'Respuesta GPT',
    inputPlaceholder: 'Pregúntame lo que quieras',
    send: 'Enviar',
    alertTitle: 'Aviso',
    confirm: 'Aceptar',
    originalUrl: 'URL original',
    localFile: 'Archivo local',
    viewFile: 'Ver archivo',
    download: 'Descargar',
    kakaoSent: 'Enviado desde KakaoTalk',
    kakaoQuestion: 'pregunta',
    kakaoAnswer: 'respuesta',
    processingTime: 'Tiempo de proceso',
    seconds: 's',
    kakaoMode: 'Modo de integración KakaoTalk',
    kakaoLoaded: 'Se ha cargado la respuesta a la pregunta enviada desde KakaoTalk. Puede continuar haciendo preguntas adicionales.',
    kakaoAnswerLoadedAlert: (q) => `Se ha cargado la respuesta a tu pregunta de KakaoTalk "${q}". Puedes continuar con más preguntas.`,
    kakaoLoadError: 'No se pudo cargar la respuesta de KakaoTalk. Por favor, actualice la página e inténtelo de nuevo.',
  },
  pt: {
    greeting: 'Olá! Como posso ajudá-lo?',
    responseLanguage: 'Idioma da resposta',
    loadingKakao: 'Carregando dados de resposta do KakaoTalk...',
    generatingResponse: 'Gerando resposta...',
    referenceDocs: 'Documentos de referência',
    page: 'Página',
    accuracy: 'Precisão',
    noDocsPlaceholder: 'Os documentos de referência relacionados aparecerão aqui quando você inserir uma pergunta.',
    kbAnswer: 'Resposta da base de conhecimento',
    gptAnswer: 'Resposta GPT',
    inputPlaceholder: 'Pergunte qualquer coisa',
    send: 'Enviar',
    alertTitle: 'Aviso',
    confirm: 'OK',
    originalUrl: 'URL original',
    localFile: 'Arquivo local',
    viewFile: 'Ver arquivo',
    download: 'Baixar',
    kakaoSent: 'Enviado pelo KakaoTalk',
    kakaoQuestion: 'pergunta',
    kakaoAnswer: 'resposta',
    processingTime: 'Tempo de processamento',
    seconds: 's',
    kakaoMode: 'Modo de integração KakaoTalk',
    kakaoLoaded: 'A resposta para a pergunta enviada do KakaoTalk foi carregada. Você pode continuar fazendo perguntas adicionais.',
    kakaoAnswerLoadedAlert: (q) => `A resposta para sua pergunta do KakaoTalk "${q}" foi carregada. Você pode continuar fazendo perguntas.`,
    kakaoLoadError: 'Falha ao carregar a resposta do KakaoTalk. Atualize a página e tente novamente.',
  },
  mn: {
    greeting: 'Сайн байна уу! Би танд юу тусалж чадах вэ?',
    responseLanguage: 'Хариулах хэл',
    loadingKakao: 'KakaoTalk-ийн хариулт ачаалж байна...',
    generatingResponse: 'Хариулт боловсруулж байна...',
    referenceDocs: 'Лавлах баримт бичиг',
    page: 'Хуудас',
    accuracy: 'Нарийвчлал',
    noDocsPlaceholder: 'Асуулт оруулбал холбогдох лавлах баримт бичгүүд энд харагдана.',
    kbAnswer: 'Мэдлэгийн санд суурилсан хариулт',
    gptAnswer: 'GPT хариулт',
    inputPlaceholder: 'Юу ч асуугаарай',
    send: 'Илгээх',
    alertTitle: 'Мэдэгдэл',
    confirm: 'OK',
    originalUrl: 'Анхны URL',
    localFile: 'Локал файл',
    viewFile: 'Файл харах',
    download: 'Татаж авах',
    kakaoSent: 'KakaoTalk-аас илгээсэн',
    kakaoQuestion: 'асуулт',
    kakaoAnswer: 'хариулт',
    processingTime: 'Боловсруулах хугацаа',
    seconds: 'с',
    kakaoMode: 'KakaoTalk интеграцийн горим',
    kakaoLoaded: 'KakaoTalk-аас илгээсэн асуултын хариулт ачааллагдлаа. Та нэмэлт асуулт асуух боломжтой.',
    kakaoAnswerLoadedAlert: (q) => `KakaoTalk-ийн таны "${q}" асуултын хариулт ачааллагдлаа. Та нэмэлт асуулт хийж болно.`,
    kakaoLoadError: 'KakaoTalk-ийн хариулт ачаалахад алдаа гарлаа. Хуудсыг шинэчилж дахин оролдоно уу.',
  },
};

// 언어별 폰트 패밀리 설정
const getLanguageFont = (lang) => {
  if (lang === 'ru') return "'Calibri', 'Times New Roman', sans-serif";
  if (lang === 'mn') return "'Arial', sans-serif";
  return 'inherit';
};

// 컴포넌트 마운트 전 브라우저 언어 감지 (useState 초기값으로 사용)
const _initialLang = getBrowserLanguage();

const UserChatPage = () => {
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: TRANSLATIONS[_initialLang].greeting, // 🌍 브라우저 언어로 초기 인사말
      id: 'initial-greeting'
    }
  ]);
  const [greetingMessage, setGreetingMessage] = useState(TRANSLATIONS[_initialLang].greeting);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [knowledgeBases, setKnowledgeBases] = useState([]);
  const [selectedKBs, setSelectedKBs] = useState([]);
  const [useStreaming, setUseStreaming] = useState(
    process.env.REACT_APP_ENABLE_STREAMING === 'true'
  );
  const [llmModels, setLlmModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState('gpt-4o-mini');
  const [selectedLanguage, setSelectedLanguage] = useState(_initialLang); // 🌍 브라우저 언어 자동 감지
  const [sourceDocuments, setSourceDocuments] = useState([]);
  const chatEndRef = useRef(null);
  const [isKBLoading, setIsKBLoading] = useState(true);
  const [isModelsLoading, setIsModelsLoading] = useState(true);
  const { startGuide, stopGuide } = useGuide();
  
  // 가이드 표시 여부를 관리하는 상태 추가
  const [hasShownGuide, setHasShownGuide] = useState(false);
  
  // 알림창 상태 관리
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');

  const location = useLocation();
  
  // 카카오톡 연동 관련 상태
  const [isFromKakao, setIsFromKakao] = useState(false);
  const [kakaoAnswerId, setKakaoAnswerId] = useState(null);
  const [isLoadingKakaoData, setIsLoadingKakaoData] = useState(false);
  const [kakaoDataLoaded, setKakaoDataLoaded] = useState(false);

  // 답변 모드 설정 (지식베이스 전용 vs GPT 일반 지식 허용)
  const [useKnowledgeBaseOnly, setUseKnowledgeBaseOnly] = useState(true);
  const [useGPTKnowledge, setUseGPTKnowledge] = useState(false);

  // 방문자 세션 관리 (localStorage visitor_id 기반)
  const [visitorId, setVisitorId] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [pastSessions, setPastSessions] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const sessionIdRef = useRef(null); // pagehide 이벤트에서 사용
  const sessionInitRef = useRef(false); // StrictMode 이중 실행 방지

  // // 페이지 로드 시 자동으로 가이드 표시 - 컴포넌트 상태로 관리
  // useEffect(() => {
  //   // 이미 가이드를 표시했다면 더 이상 표시하지 않음
  //   if (!hasShownGuide) {
  //     // 약간의 지연 후 가이드 시작 (컴포넌트가 완전히 렌더링된 후)
  //     const timer = setTimeout(() => {
  //       startGuide('chat');
  //       // 가이드를 표시한 것으로 상태 업데이트
  //       setHasShownGuide(true);
  //     }, 500);
      
  //     return () => clearTimeout(timer);
  //   }
  // }, [startGuide, hasShownGuide]);

  // 커스텀 가이드 종료 핸들러 - 필요한 경우 사용
  const handleGuideExit = () => {
    stopGuide();
    setHasShownGuide(true);
  };

  // 알림창 표시 함수
  const showAlert = (message) => {
    setAlertMessage(message);
    setAlertOpen(true);
  };

  // 알림창 닫기 함수
  const handleAlertClose = () => {
    setAlertOpen(false);
  };

  // 카카오톡 답변 데이터 로드 함수
  const loadKakaoAnswerData = async (answerId) => {
    try {
      setIsLoadingKakaoData(true);
      console.log(`[카카오톡 연동] 답변 데이터 로드 시작: ${answerId}`);
      
      const response = await fetch(`https://gpt.buildupkorea.com:8800/api/chat/answer-data/${answerId}`);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const answerData = await response.json();
      console.log('[카카오톡 연동] 답변 데이터 로드 완료:', answerData);
      
      // 채팅창에 카카오톡 질문과 답변 추가
      const kakaoMessages = [
        {
          role: 'user',
          content: answerData.question,
          isFromKakao: true,
          timestamp: answerData.created_at
        },
        {
          role: 'assistant',
          content: answerData.answer,
          isFromKakao: true,
          sources: answerData.sources || [],
          processingTime: answerData.processing_time,
          timestamp: answerData.created_at
        }
      ];
      
      setMessages(kakaoMessages);
      setSourceDocuments(answerData.sources || []);
      setKakaoDataLoaded(true);
      
      // 성공 알림 표시
      showAlert(TRANSLATIONS[selectedLanguage].kakaoAnswerLoadedAlert(answerData.question));
      
    } catch (error) {
      console.error('[카카오톡 연동] 답변 데이터 로드 실패:', error);
      setError(`카카오톡 답변을 불러올 수 없습니다: ${error.message}`);
      showAlert(TRANSLATIONS[selectedLanguage].kakaoLoadError);
    } finally {
      setIsLoadingKakaoData(false);
    }
  };

  // 시스템 설정 로드 (브라우저 언어 감지 ON/OFF)
  useEffect(() => {
    settingsApi.getPublicSettings()
      .then((settings) => {
        const detectionEnabled = settings.browser_language_detection !== false;
        const newCacheValue = detectionEnabled ? 'true' : 'false';
        localStorage.setItem('sys_browser_language_detection', newCacheValue);
        // 감지 비활성화 시 한국어로 강제 변경
        if (!detectionEnabled) {
          setSelectedLanguage('ko');
        }
      })
      .catch(() => {}); // 서버 오류 시 기본 동작 유지
  }, []);

  // URL 파라미터 확인 및 카카오톡 데이터 로드
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const fromKakao = urlParams.get('from_kakao');
    const answerId = urlParams.get('answer_id');
    
    console.log('[카카오톡 연동] URL 파라미터 확인:', { fromKakao, answerId });
    
    if (fromKakao === 'true' && answerId && !kakaoDataLoaded) {
      setIsFromKakao(true);
      setKakaoAnswerId(answerId);
      loadKakaoAnswerData(answerId);
    }
  }, [location.search, kakaoDataLoaded]);

  useEffect(() => {
    // 지식베이스 목록 가져오기
    const fetchKnowledgeBases = async () => {
      try {
        setIsKBLoading(true);
        const [response, publicSettings] = await Promise.all([
          knowledgeBaseApi.listKnowledgeBases(),
          settingsApi.getPublicSettings().catch(() => ({})),
        ]);
        // 준비된 지식베이스만 필터링
        const readyKBs = response.knowledge_bases.filter(kb => kb.status === 'ready');
        setKnowledgeBases(readyKBs);

        // 관리자가 설정한 chat_kb_ids 우선 적용
        const configuredIds = publicSettings.chat_kb_ids;
        if (Array.isArray(configuredIds) && configuredIds.length > 0) {
          const validIds = configuredIds.filter(id => readyKBs.some(kb => kb.kb_id === id));
          setSelectedKBs(validIds.length > 0 ? validIds : (readyKBs.length > 0 ? [readyKBs[0].kb_id] : []));
        } else if (readyKBs.length > 0) {
          setSelectedKBs([readyKBs[0].kb_id]);
        }

        // 관리자 설정 인사말 적용
        if (publicSettings.chat_greeting) {
          setGreetingMessage(publicSettings.chat_greeting);
          setMessages(prev => {
            if (prev.length === 1 && prev[0].id === 'initial-greeting') {
              return [{ role: 'assistant', content: publicSettings.chat_greeting, id: 'initial-greeting' }];
            }
            return prev;
          });
        }
      } catch (error) {
        console.error('지식베이스를 가져오는 중 오류 발생:', error);
        setError('지식베이스 목록을 불러올 수 없습니다.');
      } finally {
        setIsKBLoading(false);
      }
    };

    // LLM 모델 목록 가져오기
    const fetchLLMModels = async () => {
      try {
        setIsModelsLoading(true);
        const response = await modelsApi.listLlmModels();
        setLlmModels(response.models);
      } catch (error) {
        console.error('LLM 모델을 가져오는 중 오류 발생:', error);
        setError('LLM 모델 목록을 불러올 수 없습니다.');
      } finally {
        setIsModelsLoading(false);
      }
    };

    fetchKnowledgeBases();
    fetchLLMModels();
  }, []);

  useEffect(() => {
    // 지식베이스나 모델이 변경될 때만 대화 내용 초기화
    setInput('');
    setMessages([
      {
        role: 'assistant',
        content: greetingMessage,
        id: 'initial-greeting'
      }
    ]);
    setSourceDocuments([]);
    console.log("지식베이스 또는 모델 변경으로 대화 내용 초기화");
  }, [selectedKBs, selectedModel, greetingMessage]);

  // ── 방문자 세션 초기화 ───────────────────────────────────────────
  useEffect(() => {
    if (sessionInitRef.current) return; // StrictMode 이중 실행 방지
    sessionInitRef.current = true;

    // visitor_id: localStorage에서 가져오거나 새로 생성
    let vid = localStorage.getItem('rag_visitor_id');
    if (!vid) {
      vid = 'v-' + crypto.randomUUID();
      localStorage.setItem('rag_visitor_id', vid);
    }
    setVisitorId(vid);

    // 새 session_id 생성
    const sid = 's-' + crypto.randomUUID();
    setSessionId(sid);
    sessionIdRef.current = sid;

    // 세션 시작 API 호출
    chatApi.startVisitorSession(sid, vid).catch(console.error);

    // 과거 세션 목록 로드
    chatApi.listVisitorSessions(vid).then(data => {
      setPastSessions(data.sessions || []);
    }).catch(console.error);

    // 탭/브라우저 닫힐 때 세션 종료 (sendBeacon 사용)
    const handlePageHide = () => {
      if (sessionIdRef.current) {
        chatApi.endVisitorSession(sessionIdRef.current);
      }
    };
    window.addEventListener('pagehide', handlePageHide);
    return () => window.removeEventListener('pagehide', handlePageHide);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // 과거 세션 목록 새로고침 헬퍼
  const refreshPastSessions = useCallback(async (vid) => {
    if (!vid) return;
    try {
      const data = await chatApi.listVisitorSessions(vid);
      setPastSessions(data.sessions || []);
    } catch (e) {
      console.error('과거 세션 목록 조회 실패', e);
    }
  }, []);

  // 특정 세션 메시지 복원
  const loadPastSession = async (sess) => {
    try {
      setLoadingHistory(true);
      const data = await chatApi.getVisitorSessionMessages(sess.session_id);
      const restored = [];
      for (const m of data.messages) {
        restored.push({ role: 'user',      content: m.user,      id: `h-u-${m.id}` });
        restored.push({ role: 'assistant', content: m.assistant, id: `h-a-${m.id}` });
      }
      if (restored.length === 0) {
        restored.push({ role: 'assistant', content: greetingMessage, id: 'initial-greeting' });
      }
      setMessages(restored);
      setShowHistory(false);
    } catch (e) {
      console.error('세션 복원 실패', e);
    } finally {
      setLoadingHistory(false);
    }
  };

  // 과거 세션 삭제
  const deletePastSession = async (e, sess) => {
    e.stopPropagation();
    if (!visitorId) return;
    try {
      await chatApi.deleteVisitorSession(sess.session_id, visitorId);
      setPastSessions(prev => prev.filter(s => s.session_id !== sess.session_id));
    } catch (e) {
      console.error('세션 삭제 실패', e);
    }
  };

  const handleInputChange = (e) => {
    setInput(e.target.value);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      if (e.shiftKey) {
        // Shift + Enter는 줄바꿈 (기본 동작 유지)
        return;
      }
      
      // Enter만 누를 경우 폼 제출
      e.preventDefault();
      if (input.trim()) {
        handleSubmit(e);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;

    // 지식베이스 선택 확인
    if (!selectedKBs.length) {
      showAlert('하나 이상의 지식베이스를 선택해주세요. 지식베이스 없이는 질문에 답변할 수 없습니다.');
      return;
    }

    const userMessage = { role: 'user', content: input };
    
    // 기존 메시지를 유지하면서 새 메시지 추가
    setMessages((prevMessages) => [...prevMessages, userMessage]);
    setInput('');
    setSourceDocuments([]);
    
    try {
      setIsLoading(true);
      setError(null);

      // 요청에 타임스탬프와 고유 ID를 추가하여 캐싱 방지
      const requestId = `req-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
      
      // 이전 대화 내역을 포함하여 문맥 유지 (최대 5개 메시지로 제한)
      // 새로운 사용자 메시지를 추가하기 전의 메시지들을 계산
      const previousMessages = messages.slice(-10);
      
      const chatData = {
        // 이전 메시지 내역 + 현재 질문을 함께 전달
        messages: [...previousMessages, userMessage],
        kb_ids: selectedKBs,
        model: selectedModel,
        use_semantic_search: true,
        use_streaming: useStreaming,
        request_id: requestId,
        response_language: selectedLanguage, // 🌍 응답 언어 전달
        timestamp: Date.now(),
        maintain_context: true, // 문맥 유지를 위한 플래그 추가
        allow_general_knowledge: useGPTKnowledge, // 🔥 GPT 일반 지식 사용 여부 (사용자 UI 설정)
        session_id: sessionId,    // 방문자 세션 추적
        visitor_id: visitorId,    // 비로그인 방문자 ID
      };

      console.log(`요청 시작 ID: ${requestId}, 타임스탬프: ${chatData.timestamp}, 메시지 수: ${chatData.messages.length}`);

      if (useStreaming) {
        // 스트리밍 처리
        let assistantResponse = '';
        let tempId = Date.now().toString();
        
        // 임시 메시지 추가
        setMessages((prevMessages) => [
          ...prevMessages,
          { role: 'assistant', content: '', id: tempId },
        ]);

        // 스트림 연결 저장 변수
        let streamConnection;

        try {
          streamConnection = await chatApi.sendStreamingChatMessage(
            chatData,
            (data) => {
              // 응답 메시지 처리
              assistantResponse = data.response;
              
              // 🌍 자동 언어 감지 - 응답에서 감지된 언어로 드롭다운 업데이트
              if (data.detected_language && data.detected_language !== selectedLanguage) {
                console.log(`언어 자동 감지: ${data.detected_language}`);
                setSelectedLanguage(data.detected_language);
              }
              
              // 🔍 디버그: 표가 몇 개 있는지 확인
              if (data.done && assistantResponse.includes('|')) {
                const tableCount = (assistantResponse.match(/\|[^|]+\|[^|]+\|/g) || []).length;
                console.log('🔍 최종 응답에 표 행 개수:', tableCount);
                console.log('🔍 전체 응답 내용:', assistantResponse);
              }
              
              // 메시지 업데이트 (완료 시 chart_data 도 포함)
              setMessages((prevMessages) =>
                prevMessages.map((msg) =>
                  msg.id === tempId
                    ? {
                        ...msg,
                        content: assistantResponse,
                        ...(data.done && data.chart_data ? { chartData: data.chart_data } : {}),
                      }
                    : msg
                )
              );

              // 소스 문서 처리
              if (data.sources) {
                console.log('스트리밍 응답에서 참조문서 수신:', data.sources);
                // 전체 소스 문서 정보 보존
                const processedSources = data.sources.map(source => ({
                  ...source,
                  // source_url 또는 url 필드 확인 (크롤링 시 url 필드로 저장될 수 있음)
                  source_url: (source.source_url && 
                    (source.source_url.startsWith('http://') || source.source_url.startsWith('https://'))) 
                      ? source.source_url 
                      : (source.url && 
                        (source.url.startsWith('http://') || source.url.startsWith('https://'))) 
                        ? source.url 
                        : null
                }));
                setSourceDocuments(processedSources);
              }
            },
            (error) => {
              console.error(`스트리밍 에러 (요청 ID: ${requestId}):`, error);
              setError('응답을 받는 중 오류가 발생했습니다.');
              setIsLoading(false);
            },
            () => {
              // 스트리밍 완료 후 처리
              setIsLoading(false);
              
              // 스트리밍 완료 후 약간의 시간을 두고 마크다운 헤딩 처리
              setTimeout(() => {
                setMessages((prevMessages) =>
                  prevMessages.map((msg) => {
                    if (msg.id === tempId) {
                      // 처리 함수를 사용하여 테이블과 헤더 처리
                      const processedContent = processMarkdownHeadings(msg.content);
                      
                      // 마크다운 제목 올바르게 렌더링되도록 강제 갱신 (내용은 동일하게 유지)
                      return { ...msg, id: `${tempId}-processed`, content: processedContent };
                    }
                    return msg;
                  })
                );
              }, 300); // 300ms 딜레이 후 재처리
            }
          );
        } catch (streamError) {
          console.error(`스트림 연결 오류 (요청 ID: ${requestId}):`, streamError);
          setError('스트리밍 연결 중 오류가 발생했습니다.');
          setIsLoading(false);
        }

        // 컴포넌트가 언마운트될 때 스트림 연결 종료
        return () => {
          if (streamConnection) {
            streamConnection.close();
          }
        };
      } else {
        // 비스트리밍 처리
        console.log(`비스트리밍 요청 시작 (요청 ID: ${requestId})`);
        const response = await chatApi.sendChatMessage(chatData);
        console.log(`비스트리밍 응답 수신 (요청 ID: ${requestId})`);
        
        // 마크다운 처리
        let processedContent = response.response;
        
        // processMarkdownHeadings 함수를 이용해 한번에 처리
        processedContent = processMarkdownHeadings(processedContent);
        
        const assistantMessage = {
          role: 'assistant',
          content: processedContent,
          id: `res-${Date.now()}-nonstreaming`, // 비스트리밍 메시지는 특별한 ID 사용
          chartData: response.chart_data || null,  // 📊 차트 데이터
        };
        
        setMessages((prevMessages) => [...prevMessages, assistantMessage]);
        console.log('비스트리밍 응답의 참조문서:', response.sources);
        
        // 🌍 자동 언어 감지 - 응답에서 감지된 언어로 드롭다운 업데이트
        if (response.detected_language && response.detected_language !== selectedLanguage) {
          console.log(`언어 자동 감지: ${response.detected_language}`);
          setSelectedLanguage(response.detected_language);
        }
        
        // 전체 소스 문서 정보 보존
        const processedSources = response.sources ? response.sources.map(source => ({
          ...source,
          // source_url 또는 url 필드 확인 (크롤링 시 url 필드로 저장될 수 있음)
          source_url: (source.source_url && 
            (source.source_url.startsWith('http://') || source.source_url.startsWith('https://'))) 
              ? source.source_url 
              : (source.url && 
                (source.url.startsWith('http://') || source.url.startsWith('https://'))) 
                ? source.url 
                : null
        })) : [];
        setSourceDocuments(processedSources);
      }
    } catch (error) {
      console.error('챗봇 응답 에러:', error);
      setError('메시지를 전송하는 중 오류가 발생했습니다.');
    } finally {
      if (!useStreaming) {
        setIsLoading(false);
      }
    }
  };

  const handleClearChat = () => {
    // 현재 세션 종료
    if (sessionId) {
      chatApi.endVisitorSession(sessionId).catch(console.error);
    }

    // 새 session_id 생성 및 세션 시작
    const newSid = 's-' + crypto.randomUUID();
    setSessionId(newSid);
    sessionIdRef.current = newSid;
    if (visitorId) {
      chatApi.startVisitorSession(newSid, visitorId).catch(console.error);
      // 과거 세션 목록 새로고침
      refreshPastSessions(visitorId);
    }

    setMessages([
      {
        role: 'assistant',
        content: greetingMessage,
        id: 'initial-greeting'
      }
    ]);
    setSourceDocuments([]);
  };

  // 메시지 렌더링을 위한 컴포넌트
  const renderMessage = (message, index) => {
    // 빈 말풍선 렌더링 방지 (스트리밍 시작 전 임시 메시지)
    if (!message.content && message.role === 'assistant') return null;

    const isUser = message.role === 'user';
    
    // 메시지 내용 처리
    const processContent = () => {
      const content = message.content;
      
      // 마크다운 테이블이 포함된 경우 변환 후 dangerouslySetInnerHTML 사용 (최우선 처리)
      // 🔥 테이블이 있으면 parseMarkdownTables만 사용 (processMarkdownHeadings 중복 제거)
      if (hasMarkdownTable(content)) {
        const processedContent = parseMarkdownTables(content);
        return (
          <div 
            dangerouslySetInnerHTML={{ __html: processedContent }}
            ref={(el) => {
              if (el) {
                console.log('🔄 테이블 포함 DOM 렌더링 완료, 중복 테이블 제거 시작');
                
                // 중복 테이블 제거 - 즉시 실행
                removeDuplicateTables(el);
                
                // 짧은 지연 후 한 번 더 실행 (DOM 안정화 대기)
                setTimeout(() => {
                  console.log('🔄 5ms 후 중복 테이블 제거 재시도');
                  removeDuplicateTables(el);
                }, 5);
                
                // 추가 안전장치 - 더 긴 지연 후 한 번 더 실행
                setTimeout(() => {
                  console.log('🔄 50ms 후 중복 테이블 제거 최종 확인');
                  removeDuplicateTables(el);
                }, 50);
              }
            }}
          />
        );
      }
      
      // HTML 태그가 포함된 경우 dangerouslySetInnerHTML 사용
      if (hasHtmlTags(content)) {
        return (
          <div 
            dangerouslySetInnerHTML={{ __html: content }}
            ref={(el) => {
              if (el) {
                // HTML에 테이블이 있을 수 있으므로 중복 제거 시도
                const tables = el.querySelectorAll('table');
                if (tables.length > 1) {
                  console.log('🔄 HTML 태그 내 테이블 중복 제거 시작');
                  removeDuplicateTables(el);
                  setTimeout(() => removeDuplicateTables(el), 5);
                  setTimeout(() => removeDuplicateTables(el), 50);
                }
              }
            }}
          />
        );
      }
      
      // 응답자 메시지인 경우 항상 마크다운 헤딩 전처리 시도
      if (message.role === 'assistant') {
        const processedContent = processMarkdownHeadings(content);
        
        // 헤딩 패턴이 포함되어 있거나 처리 후 변경된 경우에는 HTML로 렌더링
        if (hasMarkdownHeadings(content) || processedContent !== content) {
          return (
            <div 
              dangerouslySetInnerHTML={{ __html: processedContent }}
              ref={(el) => {
                if (el) {
                  // HTML에 테이블이 있을 수 있으므로 중복 제거 시도
                  const tables = el.querySelectorAll('table');
                  if (tables.length > 1) {
                    console.log('🔄 헤딩 처리된 HTML 내 테이블 중복 제거 시작');
                    removeDuplicateTables(el);
                    setTimeout(() => removeDuplicateTables(el), 5);
                    setTimeout(() => removeDuplicateTables(el), 50);
                  }
                }
              }}
            />
          );
        }
      }
      
      // ReactMarkdown의 헤딩 컴포넌트 커스터마이징
      const headingRenderer = ({ level, children }) => {
        const HeadingTag = `h${level}`;
        return <HeadingTag>{children}</HeadingTag>;
      };
      
      // 일반 마크다운은 ReactMarkdown으로 렌더링
      return (
        <ReactMarkdown
          rehypePlugins={[rehypeRaw]}
          components={{
            h1: headingRenderer,
            h2: headingRenderer,
            h3: headingRenderer,
            h4: headingRenderer,
            h5: headingRenderer,
            h6: headingRenderer,
            code({node, inline, className, children, ...props}) {
              const match = /language-(\w+)/.exec(className || '')
              return !inline && match ? (
                <SyntaxHighlighter
                  style={materialDark}
                  language={match[1]}
                  PreTag="div"
                  {...props}
                >
                  {String(children).replace(/\n$/, '')}
                </SyntaxHighlighter>
              ) : (
                <code className={className} {...props}>
                  {children}
                </code>
              )
            }
          }}
        >
          {content}
        </ReactMarkdown>
      );
    };
    
    return (
      <Box
        key={index}
        sx={{
          display: 'flex',
          justifyContent: isUser ? 'flex-end' : 'flex-start',
          mb: 2,
        }}
      >
        {/* 카카오톡에서 온 메시지인 경우 상단에 표시 */}
        {message.isFromKakao && (
          <Box sx={{ width: '100%', mb: 1 }}>
            <Alert 
              severity="info" 
              sx={{ 
                mb: 1,
                '& .MuiAlert-message': { width: '100%' }
              }}
            >
              <AlertTitle>{TRANSLATIONS[selectedLanguage].kakaoSent} {isUser ? TRANSLATIONS[selectedLanguage].kakaoQuestion : TRANSLATIONS[selectedLanguage].kakaoAnswer}</AlertTitle>
              {message.timestamp && (
                <Typography variant="caption" color="text.secondary">
                  {new Date(message.timestamp).toLocaleString('ko-KR')}
                </Typography>
              )}
              {!isUser && message.processingTime && (
                <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                  {TRANSLATIONS[selectedLanguage].processingTime}: {message.processingTime}{TRANSLATIONS[selectedLanguage].seconds}
                </Typography>
              )}
            </Alert>
          </Box>
        )}
        
        <Paper
          elevation={1}
          sx={{
            p: 2,
            maxWidth: isUser ? '80%' : '95%',
            width: isUser ? 'auto' : '95%',
            backgroundColor: message.isFromKakao 
              ? (isUser ? '#fff3e0' : '#e8f5e8')  // 카카오톡 메시지는 다른 색상
              : (isUser ? '#e3f2fd' : '#f5f5f5'),
            borderRadius: isUser ? '16px 16px 0 16px' : '16px 16px 16px 0',
            border: message.isFromKakao ? '2px solid #ff9800' : 'none',
          }}
        >
          <Typography variant="body1" component="div" className="markdown-content" sx={{ fontFamily: getLanguageFont(selectedLanguage) }}>
            {processContent()}
          </Typography>
          {/* 📊 통계 차트 (chart_data 가 있을 때만 렌더링) */}
          {!isUser && message.chartData && (
            <StatsChart chartData={message.chartData} />
          )}
        </Paper>
      </Box>
    );
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 120px)' }}>
      {/* 카카오톡 연동 상태 표시 */}
      {isFromKakao && (
        <Alert 
          severity="success" 
          sx={{ mb: 2 }}
          action={
            kakaoAnswerId && (
              <Typography variant="caption" color="text.secondary">
                ID: {kakaoAnswerId.substring(0, 8)}...
              </Typography>
            )
          }
        >
          <AlertTitle>{TRANSLATIONS[selectedLanguage].kakaoMode}</AlertTitle>
          {TRANSLATIONS[selectedLanguage].kakaoLoaded}
        </Alert>
      )}
      
      {/* 카카오톡 데이터 로딩 중 표시 */}
      {isLoadingKakaoData && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <CircularProgress size={20} sx={{ mr: 1 }} />
            {TRANSLATIONS[selectedLanguage].loadingKakao}
          </Box>
        </Alert>
      )}
      
      {/* 🌍 응답 언어 선택 드롭다운 - 고객사 요청으로 숨김 처리 (2026-03-01)
           백엔드(services/chat.py)에서 detect_language(query)로 질문 언어를 자동 감지하여
           response_language를 덮어쓰기 때문에 드롭다운 값은 실제로 사용되지 않음.
           selectedLanguage 상태는 UI 다국어 표시(TRANSLATIONS)에는 여전히 사용됨.
      <Box sx={{ mb: 2, maxWidth: 300 }}>
        <FormControl fullWidth size="small">
          <InputLabel>{TRANSLATIONS[selectedLanguage].responseLanguage}</InputLabel>
          <Select
            value={selectedLanguage}
            label={TRANSLATIONS[selectedLanguage].responseLanguage}
            onChange={(e) => setSelectedLanguage(e.target.value)}
          >
            {LANGUAGE_OPTIONS.map(lang => (
              <MenuItem key={lang.code} value={lang.code}>
                {lang.flag} {lang.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      */}

      {/* 채팅 컨트롤 바 (이전 대화 / 초기화) */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
        <Tooltip title="이전 대화 목록">
          <IconButton
            size="small"
            onClick={() => {
              setShowHistory(true);
              if (visitorId) refreshPastSessions(visitorId);
            }}
            sx={{ border: '1px solid #e0e0e0', borderRadius: 1, px: 1 }}
          >
            <HistoryIcon fontSize="small" sx={{ mr: 0.5 }} />
            <Typography variant="caption" sx={{ display: { xs: 'none', sm: 'inline' } }}>
              이전 대화
            </Typography>
          </IconButton>
        </Tooltip>

        <Tooltip title="새 대화 시작">
          <IconButton
            size="small"
            onClick={handleClearChat}
            sx={{ border: '1px solid #e0e0e0', borderRadius: 1, px: 1, ml: 'auto' }}
          >
            <AddCommentIcon fontSize="small" sx={{ mr: 0.5 }} />
            <Typography variant="caption" sx={{ display: { xs: 'none', sm: 'inline' } }}>
              새 대화
            </Typography>
          </IconButton>
        </Tooltip>
      </Box>

      {/* 기존 설정들 (숨김 처리) */}
      <Box sx={{ display: 'flex', mb: 2 }}>
        <Grid container spacing={2} style={{ display: 'none' }}>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small" disabled={isKBLoading} data-guide="knowledge-base-selector">
              <InputLabel>지식베이스</InputLabel>
              <Select
                multiple
                value={selectedKBs}
                label="지식베이스"
                onChange={(e) => setSelectedKBs(e.target.value)}
                renderValue={(selected) => selected.length > 0 ? `${selected.length}개 선택됨` : ''}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 250
                    }
                  }
                }}
              >
                {knowledgeBases.map((kb) => (
                  <MenuItem key={kb.kb_id} value={kb.kb_id}>
                    {kb.name}
                  </MenuItem>
                ))}
              </Select>
               
              {/* 선택된 지식베이스를 별도로 표시 */}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                {selectedKBs.map((value) => {
                  const kb = knowledgeBases.find(kb => kb.kb_id === value);
                  return (
                    <Chip 
                      key={value} 
                      label={kb ? kb.name : value} 
                      size="small"
                      onDelete={() => {
                        const newSelected = selectedKBs.filter(id => id !== value);
                        setSelectedKBs(newSelected);
                      }}
                      sx={{ 
                        m: 0.5, 
                        '& .MuiChip-label': { 
                          fontSize: '1rem',
                          fontWeight: 'medium',
                          color: '#333',
                          padding: '0 10px'
                        }
                      }}
                    />
                  );
                })}
              </Box>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small" disabled={isModelsLoading}>
              <InputLabel>LLM 모델</InputLabel>
              <Select
                value={selectedModel}
                label="LLM 모델"
                onChange={(e) => setSelectedModel(e.target.value)}
              >
                {llmModels.map((model) => (
                  <MenuItem key={model.id} value={model.id}>
                    {model.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={useStreaming}
                    onChange={(e) => setUseStreaming(e.target.checked)}
                  />
                }
                label="스트리밍 응답"
              />
              <Button
                variant="outlined"
                startIcon={<DeleteIcon />}
                onClick={handleClearChat}
                sx={{ ml: 'auto' }}
              >
                대화 초기화
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>

      <Grid container spacing={2} sx={{ flexGrow: 1, mb: 2, height: 'calc(70vh - 120px)' }}>
        {/* Chat Messages Area */}
        <Grid item xs={12} sx={{ height: '100%' }}>
          <Paper
            elevation={3}
            sx={{
              height: '100%',
              p: 2,
              overflowY: 'auto',
              bgcolor: '#fafafa',
              display: 'flex',
              flexDirection: 'column',
            }}
            data-guide="chat-history"
          >
            {messages.map(renderMessage)}
            
            {isLoading && useStreaming && (
              <Box sx={{ display: 'flex', alignItems: 'center', my: 2 }}>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  {TRANSLATIONS[selectedLanguage].generatingResponse}
                </Typography>
              </Box>
            )}
            
            {error && <ErrorMessage message={error} />}
            
            <div ref={chatEndRef} />
          </Paper>
        </Grid>


      </Grid>

      <Paper elevation={3} sx={{ p: 1 }}>
        <form onSubmit={handleSubmit}>
          {/* 답변 모드 선택 버튼 */}
          <Box sx={{ 
            display: 'flex', 
            gap: 1, 
            mb: 1.5,
            alignItems: 'center'
          }}>
            <Chip
              label={TRANSLATIONS[selectedLanguage].kbAnswer}
              sx={{
                bgcolor: '#1a1a2e',
                color: '#fff',
                border: '2px solid #1a1a2e',
                fontWeight: 'bold',
                cursor: 'default',
                '&:hover': {
                  bgcolor: '#1a1a2e',
                }
              }}
            />
            <Chip
              label={TRANSLATIONS[selectedLanguage].gptAnswer}
              onClick={() => {
                setUseGPTKnowledge(!useGPTKnowledge);
              }}
              sx={{
                bgcolor: useGPTKnowledge ? '#7c3aed' : 'transparent',
                color: useGPTKnowledge ? '#fff' : '#666',
                border: useGPTKnowledge ? '2px solid #7c3aed' : '2px solid #ddd',
                fontWeight: useGPTKnowledge ? 'bold' : 'normal',
                cursor: 'pointer',
                transition: 'all 0.2s',
                '&:hover': {
                  bgcolor: useGPTKnowledge ? '#7c3aed' : 'rgba(124, 58, 237, 0.05)',
                  borderColor: '#7c3aed',
                }
              }}
            />
          </Box>

          <Box sx={{ display: 'flex' }}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder={TRANSLATIONS[selectedLanguage].inputPlaceholder}
              value={input}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              disabled={isLoading || !selectedKBs.length}
              multiline
              maxRows={3}
              sx={{ mr: 1 }}
              data-guide="chat-input"
            />
            <Button
              type="submit"
              variant="contained"
              disabled={isLoading || !input.trim() || !selectedKBs.length}
              endIcon={isLoading && !useStreaming ? <CircularProgress size={20} /> : <SendIcon />}
              sx={{ 
                whiteSpace: 'nowrap',
                color: '#ffffff !important',
                fontWeight: '600',
                textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)',
                '& .MuiSvgIcon-root': {
                  color: '#ffffff !important'
                }
              }}
            >
              {TRANSLATIONS[selectedLanguage].send}
            </Button>
          </Box>
        </form>
      </Paper>

      {/* ── 이전 대화 목록 사이드 패널 ──────────────────────── */}
      <Drawer
        anchor="left"
        open={showHistory}
        onClose={() => setShowHistory(false)}
        PaperProps={{
          sx: { width: { xs: '85vw', sm: 320 }, display: 'flex', flexDirection: 'column' }
        }}
      >
        {/* 헤더 */}
        <Box sx={{ display: 'flex', alignItems: 'center', p: 2, borderBottom: '1px solid #e0e0e0' }}>
          <HistoryIcon sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h6" sx={{ flexGrow: 1, fontSize: '1rem', fontWeight: 700 }}>
            이전 대화 목록
          </Typography>
          <IconButton size="small" onClick={() => setShowHistory(false)}>
            <ChevronLeftIcon />
          </IconButton>
        </Box>

        {/* 새 대화 버튼 */}
        <Box sx={{ p: 1.5, borderBottom: '1px solid #f0f0f0' }}>
          <Button
            fullWidth
            variant="outlined"
            startIcon={<AddCommentIcon />}
            onClick={() => { handleClearChat(); setShowHistory(false); }}
            size="small"
          >
            새 대화 시작
          </Button>
        </Box>

        {/* 세션 목록 */}
        <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
          {loadingHistory && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress size={24} />
            </Box>
          )}
          {!loadingHistory && pastSessions.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
              저장된 대화가 없습니다
            </Typography>
          )}
          <List dense disablePadding>
            {pastSessions.map((sess) => (
              <ListItem
                key={sess.session_id}
                disablePadding
                secondaryAction={
                  <Tooltip title="삭제">
                    <IconButton
                      edge="end"
                      size="small"
                      onClick={(e) => deletePastSession(e, sess)}
                      sx={{ color: 'text.disabled', '&:hover': { color: 'error.main' } }}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                }
                divider
              >
                <ListItemButton onClick={() => loadPastSession(sess)} sx={{ pr: 5 }}>
                  <ListItemText
                    primary={
                      <Typography
                        variant="body2"
                        noWrap
                        sx={{ fontWeight: 500, maxWidth: 200 }}
                      >
                        {sess.title || '채팅'}
                      </Typography>
                    }
                    secondary={
                      <Typography variant="caption" color="text.secondary">
                        {sess.message_count}개 메시지 · {new Date(sess.started_at).toLocaleDateString('ko-KR')}
                      </Typography>
                    }
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Box>
      </Drawer>

      {/* 경고 알림창 */}
      <Dialog
        open={alertOpen}
        onClose={handleAlertClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        PaperProps={{
          sx: {
            borderTop: '4px solid #f44336',
            borderRadius: '8px',
            maxWidth: '400px',
            minWidth: '300px'
          }
        }}
      >
        <DialogTitle id="alert-dialog-title" sx={{ display: 'flex', alignItems: 'center' }}>
          <WarningIcon sx={{ color: '#f44336', mr: 1 }} />
          {TRANSLATIONS[selectedLanguage].alertTitle}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {alertMessage}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ padding: '0 24px 20px 24px' }}>
          <Button
            onClick={handleAlertClose}
            variant="contained"
            autoFocus
            sx={{ 
              backgroundColor: '#f44336', 
              '&:hover': { backgroundColor: '#d32f2f' } 
            }}
          >
            {TRANSLATIONS[selectedLanguage].confirm}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserChatPage; 